import React, { useState } from "react";
import White from "../../assets/icons/popup/coin wallet.svg?react";
import Withdraw from "../../assets/icons/Withdraw.svg?react";
import BgLatto from "../../assets/images/bg-latto.jpg";
import Wallet from "../../assets/icons/popup/input-walet.svg?react";
import Spinner from "./Spinner";
import DepositSuccessPopup from "./DepositSuccessPopup";
import { WalletIcon, PiggyBank, UserIcon, NotebookIcon, Upload } from "lucide-react";
import { loadRequestApi } from "../../api/loadrequest";
import { useAuth } from "../../context/AuthContext";
import Swal from "sweetalert2";

export default function DepositPopup({ isOpen, onClose, currentBalance }) {
  const [amount, setAmount] = useState("100");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [name, setName] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [proofOfPayment, setProofOfPayment] = useState(null);
  const { tokens } = useAuth();

  if (!isOpen) return null;
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (images only)
      if (!file.type.startsWith('image/')) {
        Swal.fire({
          title: 'Invalid File Type',
          text: 'Please upload an image file (PNG, JPG, JPEG, etc.)',
          icon: 'error'
        });
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
          title: 'File Too Large',
          text: 'Please upload an image smaller than 10MB',
          icon: 'error'
        });
        return;
      }
      
      setProofOfPayment(file);
    }
  };
  
  const handleDeposit = async () => {
    setIsLoading(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('amount', amount);
      formData.append('paymentName', name);
      formData.append('paymentReferenceNo', referenceNo);
      formData.append('notes', notes);
      if (proofOfPayment) {
        formData.append('proofOfPayment', proofOfPayment);
      }

      const response = await loadRequestApi.sendLoadRequest(tokens.access.token, formData);
      
      if (response.status >= 200 && response.status < 300) {
        Swal.fire({
          title: 'Request Sent',
          text: 'Your request has been successfully sent.',
          icon: 'success'
        });
        onClose();
      } else {
        Swal.fire({
          title: 'Request Failed',
          text: 'Your request has failed to be sent.',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error('API call failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (showSuccess) {
    return <DepositSuccessPopup amount={amount} onClose={handleClose} />;
  }

  const formatBalance = (amount) => {
    if (!amount) return '0.00';
    return parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    });
  };

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 font-poppins">
          <Spinner />
        </div>
      )}

      {!isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 font-poppins">
          <div className="animate-slideInAndShake relative w-full max-w-sm overflow-hidden rounded-2xl transform transition-all duration-300">
            {/* Background with gradient */}
            <div
              className="absolute inset-0 "
              style={{
                backgroundImage: `url('${BgLatto}'), linear-gradient(108.9deg, rgba(14, 100, 209, 0.6) 0%, rgba(30, 66, 154, 0.6) 100.79%)`,
                backgroundBlendMode: "multiply",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            ></div>

            {/* Content */}
            <div className="relative p-4">
              <button
                onClick={onClose}
                className="absolute right-2 top-2 text-white/80 hover:text-white"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Balance display */}
              <div className="mb-3 flex justify-center items-center text-white">
                <White />
                <div className="flex flex-col">
                  <h5 className="text-[10px] leading-none font-normal">
                    Current Balance
                  </h5>
                  <h3 className="text-4xl font-semibold">
                    ₱{formatBalance(currentBalance)}
                  </h3>
                </div>
              </div>

              {/* Amount input */}
              <div className="mb-6 relative">
                {/* Image */}
                <WalletIcon className="absolute left-1 top-1/2 transform -translate-y-1/2" color="grey" />
                {/* Input Field */}
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-white rounded-lg py-3 px-4 pl-14 text-lg font-normal text-gray-500"
                  placeholder="Enter amount"
                />

              </div>

              {/* name input field */}
              <div className="mb-6 relative">
                <UserIcon className="absolute left-1 top-1/2 transform -translate-y-1/2" color="grey" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white rounded-lg py-3 px-4 pl-14 text-lg font-normal text-gray-500"
                  placeholder="Enter name"
                />  
              </div>

              {/* Reference No. or Bank Account No. input field */}
              <div className="mb-6 relative">
                <PiggyBank className="absolute left-1 top-1/2 transform -translate-y-1/2" color="grey" />
                <input
                  type="text"
                  value={referenceNo}
                  onChange={(e) => setReferenceNo(e.target.value)}
                  className="w-full bg-white rounded-lg py-3 px-4 pl-14 text-lg font-normal text-gray-500"
                  placeholder="Enter reference no."
                />
              </div>

              {/* notes input field */}
              <div className="mb-6 relative">
                <NotebookIcon className="absolute left-1 top-4 " color="grey" />
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full bg-white rounded-lg py-3 px-4 pl-14 text-lg font-normal text-gray-500"
                  placeholder="Enter notes"
                />
              </div>  

              {/* Proof of payment upload field */}
              <div className="mb-6 relative">
                <Upload className="absolute left-1 top-1/2 transform -translate-y-1/2" color="grey" />
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    id="proofOfPayment"
                  />
                  <label
                    htmlFor="proofOfPayment"
                    className="w-full bg-white rounded-lg py-3 px-4 pl-14 text-lg font-normal text-gray-500 border-2 border-dashed border-gray-300 cursor-pointer hover:border-gray-400 transition-colors flex items-center"
                  >
                    {proofOfPayment ? proofOfPayment.name : "Upload proof of payment"}
                  </label>
                </div>
              </div>

              {/* Deposit Button */}
              <button
                onClick={handleDeposit}
                className="w-full bg-[#0E64D1] hover:bg-blue-600 text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Withdraw />
                DEPOSIT
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
